import { Pause, PlayArrow } from "@mui/icons-material";
import PhoneCallbackOutlinedIcon from "@mui/icons-material/PhoneCallbackOutlined";
import {
	Box,
	IconButton,
	LinearProgress,
	Skeleton,
	Typography,
} from "@mui/material";
import React, {
	FC,
	memo,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";

import axios from "../../../../utils/api-client";

export interface TranscriptTurn {
	role: "agent" | "user";
	message: string;
	timeInCallSecs: number;
}

type FetchStatus = "idle" | "loading" | "ready" | "error";

const formatSeconds = (seconds: number) => {
	const total = Math.max(0, Math.round(seconds));
	const minutes = Math.floor(total / 60);
	const rest = String(total % 60).padStart(2, "0");
	return `${minutes}:${rest}`;
};

const useCallTranscript = (transcriptUrl: string) => {
	const [turns, setTurns] = useState<TranscriptTurn[]>([]);
	const [status, setStatus] = useState<FetchStatus>("loading");

	useEffect(() => {
		let isMounted = true;
		setStatus("loading");
		axios
			.get(transcriptUrl)
			.then((res) => {
				if (!isMounted) return;
				setTurns(Array.isArray(res.data) ? res.data : []);
				setStatus("ready");
			})
			.catch((err) => {
				console.error("Failed to load call transcript", err);
				if (isMounted) setStatus("error");
			});
		return () => {
			isMounted = false;
		};
	}, [transcriptUrl]);

	return { turns, status };
};

interface TurnBubbleProps {
	turn: TranscriptTurn;
	workerName: string;
	isActive: boolean;
	isClickable: boolean;
	onSeek: (timeInCallSecs: number) => void;
	activeTurnRef: React.RefObject<HTMLDivElement> | null;
}

const TurnBubble: FC<TurnBubbleProps> = memo(
	({ turn, workerName, isActive, isClickable, onSeek, activeTurnRef }) => {
		const { t } = useTranslation();
		const isAgent = turn.role === "agent";

		const handleActivate = useCallback(() => {
			if (isClickable) onSeek(turn.timeInCallSecs);
		}, [isClickable, onSeek, turn.timeInCallSecs]);

		const handleKeyDown = useCallback(
			(event: React.KeyboardEvent<HTMLDivElement>) => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					handleActivate();
				}
			},
			[handleActivate]
		);

		const bubbleSx = isAgent
			? {
					background: isActive ? "#E4EBF7" : "#F1F4F9",
					color: "#333",
					borderRadius: "2px 10px 10px 10px",
				}
			: {
					background: isActive ? "#243D71" : "#2F4D8B",
					color: "#fff",
					borderRadius: "10px 2px 10px 10px",
				};

		return (
			<Box
				ref={activeTurnRef}
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: "2px",
					alignItems: isAgent ? "flex-start" : "flex-end",
				}}
			>
				<Typography
					sx={{
						fontSize: "11px",
						fontWeight: 600,
						color: "#5F6368",
						paddingLeft: isAgent ? "32px" : 0,
					}}
				>
					{isAgent ? t("worker-requests_call-off-line") : workerName}
				</Typography>
				<Box
					sx={{
						display: "flex",
						gap: "8px",
						alignItems: "flex-start",
						maxWidth: "90%",
					}}
				>
					{isAgent ? (
						<Box
							sx={{
								width: "24px",
								height: "24px",
								borderRadius: "50%",
								background: "#2F4D8B",
								color: "#fff",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								flex: "0 0 auto",
								marginTop: "2px",
							}}
						>
							<PhoneCallbackOutlinedIcon sx={{ fontSize: "14px" }} />
						</Box>
					) : null}
					<Box
						role={isClickable ? "button" : undefined}
						tabIndex={isClickable ? 0 : undefined}
						aria-label={
							isClickable
								? t("worker-requests_play-call-from-message")
								: undefined
						}
						onClick={handleActivate}
						onKeyDown={isClickable ? handleKeyDown : undefined}
						sx={{
							...bubbleSx,
							padding: "8px 12px",
							fontSize: "13.5px",
							lineHeight: 1.45,
							boxShadow: isActive
								? "0 0 0 2px rgba(47,77,139,0.35)"
								: "none",
							cursor: isClickable ? "pointer" : "default",
							"&:hover": isClickable
								? { filter: "brightness(0.96)" }
								: {},
						}}
					>
						{turn.message}
					</Box>
				</Box>
			</Box>
		);
	}
);
TurnBubble.displayName = "TurnBubble";

const skeletonRows = [
	{ side: "left", width: "72%", height: 46 },
	{ side: "right", width: "52%", height: 38 },
	{ side: "left", width: "64%", height: 38 },
	{ side: "right", width: "44%", height: 30 },
];

const TranscriptSkeleton: FC = () => (
	<Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
		{skeletonRows.map((row, index) => (
			<Box
				key={`skeleton-${row.side}-${index}`}
				sx={{
					display: "flex",
					gap: "8px",
					alignItems: "flex-start",
					justifyContent:
						row.side === "left" ? "flex-start" : "flex-end",
				}}
			>
				{row.side === "left" ? (
					<Skeleton variant="circular" width={24} height={24} />
				) : null}
				<Skeleton
					variant="rounded"
					width={row.width}
					height={row.height}
					sx={{
						borderRadius:
							row.side === "left"
								? "2px 10px 10px 10px"
								: "10px 2px 10px 10px",
					}}
				/>
			</Box>
		))}
	</Box>
);

interface CallConversationProps {
	/** Time Off Requests view keys playback off the request + conversation */
	requestId?: number;
	/** Call Logs view keys playback off the call log entry id (works with no request) */
	callLogId?: number;
	conversationId: string;
	workerName?: string;
	/** Keeps the player pinned while the transcript scrolls in an outer container */
	stickyPlayer?: boolean;
}

const CallConversation: FC<CallConversationProps> = ({
	requestId,
	callLogId,
	conversationId,
	workerName = "Worker",
	stickyPlayer = false,
}) => {
	const { t } = useTranslation();
	// Call Logs uses the id-keyed endpoints; Time Off Requests the request-keyed ones
	const recordingUrl =
		callLogId != null
			? `${process.env.REACT_APP_API_END_POINT}/workers/callLogs/${callLogId}/recording`
			: `${process.env.REACT_APP_API_END_POINT}/workers/requests/${requestId}/recording/${conversationId}`;
	const transcriptUrl =
		callLogId != null
			? `${process.env.REACT_APP_API_END_POINT}/workers/callLogs/${callLogId}/transcript`
			: `${process.env.REACT_APP_API_END_POINT}/workers/requests/${requestId}/transcript/${conversationId}`;
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const objectUrlRef = useRef<string | null>(null);
	const isMountedRef = useRef(true);
	const pendingSeekRef = useRef<number | null>(null);
	const activeTurnRef = useRef<HTMLDivElement>(null);
	const [audioUrl, setAudioUrl] = useState<string | null>(null);
	const [audioStatus, setAudioStatus] = useState<FetchStatus>("loading");
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const { turns, status: transcriptStatus } =
		useCallTranscript(transcriptUrl);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
		};
	}, []);

	// Recording and transcript are fetched in parallel on mount; the recording
	// blob can be slow, so its player shows its own loading state meanwhile.
	const loadAudio = useCallback(() => {
		setAudioStatus("loading");
		if (objectUrlRef.current) {
			URL.revokeObjectURL(objectUrlRef.current);
			objectUrlRef.current = null;
		}
		setAudioUrl(null);
		axios
			.get(recordingUrl, { responseType: "blob" })
			.then((res) => {
				if (!isMountedRef.current) {
					return;
				}
				objectUrlRef.current = URL.createObjectURL(res.data);
				setAudioUrl(objectUrlRef.current);
				setAudioStatus("ready");
			})
			.catch((err) => {
				console.error("Failed to load call recording", err);
				if (isMountedRef.current) setAudioStatus("error");
			});
	}, [recordingUrl]);

	useEffect(() => {
		loadAudio();
	}, [loadAudio]);

	const handleTogglePlay = useCallback(() => {
		const audio = audioRef.current;
		if (!audio || audioStatus !== "ready") return;
		if (isPlaying) {
			audio.pause();
		} else {
			audio.play().catch((err) => {
				console.error("Failed to play call recording", err);
			});
		}
	}, [audioStatus, isPlaying]);

	const handleSeekToTurn = useCallback(
		(timeInCallSecs: number) => {
			// Recording may still be downloading; remember the seek so playback
			// starts there once the blob is ready (see handleLoadedMetadata).
			if (audioStatus === "loading") {
				pendingSeekRef.current = timeInCallSecs;
				return;
			}
			const audio = audioRef.current;
			if (!audio || audioStatus !== "ready") return;
			audio.currentTime = timeInCallSecs;
			audio.play().catch((err) => {
				console.error("Failed to play call recording", err);
			});
		},
		[audioStatus]
	);

	const handleSeekBar = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			const audio = audioRef.current;
			if (!audio || !duration) return;
			const rect = event.currentTarget.getBoundingClientRect();
			const ratio = (event.clientX - rect.left) / rect.width;
			audio.currentTime = Math.min(Math.max(ratio, 0), 1) * duration;
		},
		[duration]
	);

	const handleLoadedMetadata = useCallback(
		(event: React.SyntheticEvent<HTMLAudioElement>) => {
			const audio = event.currentTarget;
			setDuration(audio.duration);
			// Only auto-play when the user asked to jump to a turn while the
			// recording was still loading; a plain eager load stays paused.
			const pendingSeek = pendingSeekRef.current;
			pendingSeekRef.current = null;
			if (pendingSeek != null) {
				audio.currentTime = pendingSeek;
				audio.play().catch((err) => {
					console.error("Failed to play call recording", err);
				});
			}
		},
		[]
	);

	// The turn whose start time has most recently passed the playhead
	let activeTurnIndex = -1;
	if (audioStatus === "ready" && (isPlaying || currentTime > 0)) {
		turns.forEach((turn, index) => {
			if (turn.timeInCallSecs <= currentTime) activeTurnIndex = index;
		});
	}

	useEffect(() => {
		if (isPlaying && activeTurnRef.current) {
			activeTurnRef.current.scrollIntoView({
				block: "nearest",
				behavior: "smooth",
			});
		}
	}, [activeTurnIndex, isPlaying]);

	const isAudioAvailable = audioStatus !== "error";
	const playerSx: Record<string, unknown> = {
		display: "flex",
		alignItems: "center",
		gap: "14px",
		background: "#F5F7FB",
		border: "1px solid rgba(47,77,139,0.15)",
		borderRadius: "8px",
		padding: "10px 14px",
	};
	const playerWrapperSx: Record<string, unknown> = stickyPlayer
		? {
				position: "sticky",
				top: 0,
				zIndex: 2,
				background: "#fff",
				paddingBottom: "4px",
			}
		: {};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: "12px",
				fontFamily: "Roboto",
			}}
		>
			<Box sx={playerWrapperSx}>
				<Box sx={playerSx}>
					{audioUrl ? (
						<audio
							ref={audioRef}
							src={audioUrl}
							onPlay={(e) => {
								// Only one call recording should play at a time
								document
									.querySelectorAll("audio")
									.forEach((element) => {
										if (element !== e.currentTarget) {
											element.pause();
										}
									});
								setIsPlaying(true);
							}}
							onPause={() => setIsPlaying(false)}
							onEnded={() => {
								setIsPlaying(false);
								setCurrentTime(0);
							}}
							onTimeUpdate={(e) =>
								setCurrentTime(e.currentTarget.currentTime)
							}
							onLoadedMetadata={handleLoadedMetadata}
						/>
					) : null}
					{audioStatus === "loading" ? (
						<>
							<Box
								sx={{
									width: "36px",
									height: "36px",
									borderRadius: "50%",
									background: "#2F4D8B",
									color: "#fff",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flex: "0 0 auto",
									opacity: 0.55,
								}}
							>
								<PlayArrow sx={{ fontSize: "20px" }} />
							</Box>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<LinearProgress
									sx={{
										height: "4px",
										borderRadius: "2px",
										backgroundColor: "#D9E0EE",
										"& .MuiLinearProgress-bar": {
											backgroundColor: "#2F4D8B",
										},
									}}
								/>
							</Box>
							<Typography
								sx={{
									fontSize: "12px",
									color: "#5F6368",
									whiteSpace: "nowrap",
								}}
							>
								{t("worker-requests_preparing-recording")}
							</Typography>
						</>
					) : isAudioAvailable ? (
						<>
							<IconButton
								onClick={handleTogglePlay}
								aria-label={
									isPlaying
										? t("worker-requests_pause-recording")
										: t("worker-requests_play-recording")
								}
								sx={{
									width: "36px",
									height: "36px",
									background: "#2F4D8B",
									color: "#fff",
									flex: "0 0 auto",
									"&:hover": { background: "#243D71" },
								}}
							>
								{isPlaying ? (
									<Pause sx={{ fontSize: "20px" }} />
								) : (
									<PlayArrow sx={{ fontSize: "20px" }} />
								)}
							</IconButton>
							<Box
								onClick={handleSeekBar}
								sx={{
									flex: 1,
									height: "4px",
									background: "#D9E0EE",
									borderRadius: "2px",
									overflow: "hidden",
									cursor: duration ? "pointer" : "default",
								}}
							>
								<Box
									sx={{
										height: "100%",
										background: "#2F4D8B",
										borderRadius: "2px",
										width: duration
											? `${(currentTime / duration) * 100}%`
											: "0%",
									}}
								/>
							</Box>
							<Typography
								sx={{
									fontSize: "12px",
									color: "#5F6368",
									whiteSpace: "nowrap",
								}}
							>
								{duration
									? `${formatSeconds(
										currentTime
									)} / ${formatSeconds(duration)}`
									: t("worker-requests_recording")}
							</Typography>
						</>
					) : (
						<Typography sx={{ fontSize: "13px", color: "#5F6368" }}>
							{t("worker-requests_recording-not-available")}
						</Typography>
					)}
				</Box>
			</Box>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: "12px",
				}}
			>
				{transcriptStatus === "loading" ? <TranscriptSkeleton /> : null}
				{transcriptStatus === "error" ? (
					<Typography sx={{ fontSize: "13px", color: "#5F6368" }}>
						{t("worker-requests_transcript-not-available")}
					</Typography>
				) : null}
				{transcriptStatus === "ready" && turns.length === 0 ? (
					<Typography sx={{ fontSize: "13px", color: "#5F6368" }}>
						{t("worker-requests_no-spoken-messages")}
					</Typography>
				) : null}
				{transcriptStatus === "ready"
					? turns.map((turn, index) => (
							<TurnBubble
								key={`${turn.role}-${turn.timeInCallSecs}-${index}`}
								turn={turn}
								workerName={workerName}
								isActive={index === activeTurnIndex}
								isClickable={
									isAudioAvailable &&
									Number.isFinite(turn.timeInCallSecs)
								}
								onSeek={handleSeekToTurn}
								activeTurnRef={
									index === activeTurnIndex ? activeTurnRef : null
								}
							/>
					))
					: null}
			</Box>
		</Box>
	);
};

export default CallConversation;
